import React, { useEffect, useMemo, useState } from "react"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { Alert, Button, Col, Input, Row } from "reactstrap"
import accessToken from "helpers/jwt-token-access/accessToken"
import { fetchTenantPublicInfo } from "services/publicPageApi"
import { showAlert } from "store/reducers/alertReducer"
import {
  buildTenantPrivacyPolicyUrl,
  buildTenantTermsUrl,
} from "../utils/publicPageUtils"

const PublicPageLinks = ({ pageType, appType = "customer" }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(true)
  const [tenantUuid, setTenantUuid] = useState("")
  const [error, setError] = useState("")
  const resolvedAppType = appType === "rider" ? "rider" : "customer"

  useEffect(() => {
    let cancelled = false

    const loadPublicInfo = async () => {
      setLoading(true)
      setError("")
      try {
        const token = await accessToken()
        const data = await fetchTenantPublicInfo(token)
        if (!cancelled) {
          setTenantUuid(data?.uuid || "")
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || t("Unable to load public links"))
          setTenantUuid("")
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    loadPublicInfo()

    return () => {
      cancelled = true
    }
  }, [t])

  const links = useMemo(() => {
    if (!tenantUuid) {
      return []
    }

    const allLinks = [
      {
        key: "privacy-customer",
        pageType: "PrivacyPolicy",
        appType: "customer",
        label: t("Privacy Policy"),
        url: buildTenantPrivacyPolicyUrl(tenantUuid, "customer"),
      },
      {
        key: "privacy-rider",
        pageType: "PrivacyPolicy",
        appType: "rider",
        label: t("Privacy Policy"),
        url: buildTenantPrivacyPolicyUrl(tenantUuid, "rider"),
      },
      {
        key: "terms-customer",
        pageType: "TermsAndConditions",
        appType: "customer",
        label: t("Terms And Conditions"),
        url: buildTenantTermsUrl(tenantUuid, "customer"),
      },
      {
        key: "terms-rider",
        pageType: "TermsAndConditions",
        appType: "rider",
        label: t("Terms And Conditions"),
        url: buildTenantTermsUrl(tenantUuid, "rider"),
      },
    ]

    return allLinks.filter(link => {
      const matchesPage = !pageType || link.pageType === pageType
      const matchesApp = link.appType === resolvedAppType
      return matchesPage && matchesApp
    })
  }, [tenantUuid, pageType, resolvedAppType, t])

  const copyToClipboard = async value => {
    try {
      await navigator.clipboard.writeText(value)
      dispatch(
        showAlert({ text: t("Data copied to clipboard!"), type: "success" })
      )
    } catch (err) {
      dispatch(showAlert({ text: t("Failed to copy"), type: "failed" }))
    }
  }

  if (loading) {
    return null
  }

  return (
    <div className="mb-4">
      <h6 className="fw-bold mb-3">{t("Public links")}</h6>
      {error ? <Alert color="warning">{error}</Alert> : null}
      {!tenantUuid && !error ? (
        <Alert color="info">
          {t(
            "Public links will be available after the tenant UUID is generated. Save the tenant once to generate it."
          )}
        </Alert>
      ) : null}
      {links.map(link => (
        <Row key={link.key} className="align-items-center gy-2 mb-2">
          <Col md="3">
            <span className="fw-medium">{link.label}</span>
          </Col>
          <Col md="7">
            <Input value={link.url} readOnly />
          </Col>
          <Col md="2" className="d-flex gap-2">
            <Button
              color="primary"
              outline
              size="sm"
              onClick={() => copyToClipboard(link.url)}
            >
              <i className="mdi mdi-content-copy me-1" />
              {t("Copy")}
            </Button>
            <Button
              color="secondary"
              outline
              size="sm"
              tag="a"
              href={link.url}
              target="_blank"
              rel="noreferrer"
            >
              {t("Open")}
            </Button>
          </Col>
        </Row>
      ))}
    </div>
  )
}

PublicPageLinks.propTypes = {
  pageType: PropTypes.oneOf(["PrivacyPolicy", "TermsAndConditions"]),
  appType: PropTypes.oneOf(["customer", "rider"]),
}

export default PublicPageLinks
